import React, { useEffect, useState } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { baseApi, useUpdateIntegrationMutation } from 'src/services';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useParams } from 'react-router-dom';
import { EditIntegrationSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings';
import { EditIconSettings } from 'src/assets/icons/editIconsSettings';
import { useAppSelector, useAppDispatch } from 'src/store';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import type { ProjectIntegration } from 'src/services/projectsService';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const EditIntegrationPopup = ({
  isOpen = false,
  integration,
  disabled = false,
  trigger = (
    <div className="edit-button [&_svg]:hover:opacity-100 [&_svg]:opacity-40 [&_svg]:transition [&_svg>path]:transition duration-300">
      <EditIconSettings />
    </div>
  ),
}: {
  isOpen?: boolean;
  integration: ProjectIntegration;
  disabled?: boolean;
  trigger?: React.ReactNode;
}) => {
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [open, setOpen] = useState(isOpen);
  const dispatch = useAppDispatch();
  const notification = useNotification();
  const { id } = useParams();
  const [
    updateIntegration,
    {
      data: updateIntegrationData,
      isLoading: isLoadingUpdateIntegration,
      isError: isErrorUpdateIntegration,
    },
  ] = useUpdateIntegrationMutation();

  const loader = useLoader();

  const editIntegration = (data: SelectedIntegration) => {
    updateIntegration({
      projectId: id,
      integrationId: data.id,
      body: data,
    }).then((updateIntegrationResult) => {
      if (!('error' in updateIntegrationResult)) {
        notification.success(`Integration updated.`);
        loader.hide();
        onOpenChange(false);
      }
    });
  };

  useEffect(() => {
    if (updateIntegrationData) {
      dispatch(setSelectedIntegration({ ...selectedIntegration, ...updateIntegrationData }));
    }
  }, [updateIntegrationData]);

  useLoaderWhile(isLoadingUpdateIntegration);

  useEffect(() => {
    if (isErrorUpdateIntegration) {
      loader.hide();
    }
  }, [isErrorUpdateIntegration]);

  useEffect(() => {
    if (!open) {
      loader.hide();
    }
  }, [open]);

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      dispatch(baseApi.util.invalidateTags(['ProjectById']));
      loader.hide();
    }

    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${selectedIntegration?.friendlyName || integration.integration_data.name} Integration Settings`}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <EditIntegrationSettings
            onSubmit={editIntegration}
            onCancel={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
